import {
  BoxGeometry,
  CanvasTexture,
  Color,
  DirectionalLight,
  Euler,
  Group,
  HemisphereLight,
  LinearFilter,
  Mesh,
  MeshBasicMaterial,
  MeshStandardMaterial,
  PCFShadowMap,
  PerspectiveCamera,
  PlaneGeometry,
  Quaternion,
  Raycaster,
  RepeatWrapping,
  Scene,
  ShadowMaterial,
  SRGBColorSpace,
  Texture,
  TextureLoader,
  Vector2,
  Vector3,
  WebGLRenderer,
  type Intersection,
  type Material,
  type Object3D,
} from 'three'
import { cubicBezier } from '../runtime/easing'
import { WheelPager } from '../wheel-pager'
import { SETTLED, Scroller, Tween, approach, clamp01 } from './motion'
import {
  BOOKLET_ASPECT as ASPECT,
  READING_INSET,
  makeViewport,
  spreadRect,
  type Viewport,
} from './reading'

// The /bricks stage: every booklet in the collection as a real 3D object on
// one table. The entrance shows each series as a loose stack. Picking one
// stands its booklets up in a row, the shelf: each turned a little to the
// side so the covers show, scrolling with momentum. Picking a booklet brings
// the row close into the carousel, that booklet square to you and its
// neighbours angled away behind it; opening it lifts it to face you and
// swings its cover open, where the DOM reader takes over.
//
// World units are CSS pixels at the reading distance: a booklet is as wide
// in the world as the reader's page is on screen, and the reading pose sits
// on a plane facing the camera exactly that far away, so the open booklet
// covers the reader's pages pixel for pixel when they swap. Every other view
// is just the camera somewhere else over the same objects.

export type StageMode = 'table' | 'shelf' | 'carousel' | 'reading'
// How far the reader's pages may show: none, the right page (the cover has
// swung clear of it) or both (the cover lies flat over the left).
export type SpreadState = 'closed' | 'right' | 'open'

export interface StageBook {
  slug: string
  series: number
  cover: string
  // Spine, back cover and the fallback cover's ground.
  spine: string
  // The cover art's colour, drawn until the art itself has loaded.
  tone: string
  // The ground of the page under the cover in a spread (the first
  // right-hand page), so the reader's page takes over from it unseen.
  paper: string
  // The ground of the first page: under the cover on a phone, and on the
  // inside of the cover until the reader has painted that page there.
  front: string
  name: string
}

export interface StageListener {
  onMode: (mode: StageMode, series: number, index: number) => void
  // The carousel settled on another booklet.
  onFocus: (index: number) => void
  onSpread: (state: SpreadState) => void
}

interface Pose {
  pos: Vector3
  quat: Quaternion
  scale: Vector3
}

interface Book {
  item: StageBook
  index: number
  // Position within its series, and the series' size.
  slot: number
  count: number
  group: Object3D
  cover: Object3D
  inside: MeshBasicMaterial
  paper: MeshBasicMaterial
  face: MeshStandardMaterial
  meshes: Mesh[]
  seed: number
  lift: number
  tilt: Vector2
}

const THICK = 0.024
const BLANK_INSIDE = '#f3f1ec'
const MORPH_MS = 1250
const DEEP_MS = 900
const FLY_MS = 820
const OPEN_MS = 900
const DEG = Math.PI / 180
const FOV = 30

const ease = cubicBezier(0.65, 0, 0.35, 1)
const easeOut = cubicBezier(0.2, 0.7, 0.3, 1)
const smoothstep = (a: number, b: number, v: number) => {
  const t = clamp01((v - a) / (b - a))
  return t * t * (3 - 2 * t)
}

// A booklet whose cover image hasn't been baked yet: the series' black
// ground with its name, so the stage still reads.
function fallbackCover(item: StageBook) {
  const canvas = document.createElement('canvas')
  canvas.width = 300
  canvas.height = 400
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#141414'
  ctx.fillRect(0, 0, 300, 400)
  ctx.fillStyle = item.front
  ctx.fillRect(0, 352, 300, 48)
  ctx.fillStyle = '#ffffff'
  ctx.font = '600 22px system-ui, sans-serif'
  ctx.fillText(item.name, 22, 46, 256)
  return canvas
}

// A one-pixel texture of a flat colour, to stand in for a cover's art.
function toneTexture(color: string) {
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = 1
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = color
  ctx.fillRect(0, 0, 1, 1)
  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  return texture
}

function pageEdges() {
  const canvas = document.createElement('canvas')
  canvas.width = 4
  canvas.height = 64
  const ctx = canvas.getContext('2d')!
  ctx.fillStyle = '#f6f4ee'
  ctx.fillRect(0, 0, 4, 64)
  ctx.fillStyle = '#dfdace'
  for (let y = 0; y < 64; y += 4) ctx.fillRect(0, y, 4, 1)
  const texture = new CanvasTexture(canvas)
  texture.wrapS = texture.wrapT = RepeatWrapping
  texture.repeat.set(1, 2)
  return texture
}

// The pages under the cover: a block with its spine at x = 0 and its front
// face a hair under the cover, which lies at z = 0. A box draws once per
// face; its faces are regrouped by material (the page edges, the spine and
// back cover, the first page) so each booklet draws three times, not six.
function pageBlock() {
  const block = new BoxGeometry(1, 1 / ASPECT, THICK)
  block.translate(0.5, 0, -THICK / 2 - 0.002)
  // Box faces, in order: +x, -x, +y, -y, +z, -z; six indices each.
  const faces = [[0, 2, 3], [1, 5], [4]]
  const source = block.index!.array
  const index: number[] = []
  block.clearGroups()
  faces.forEach((group, material) => {
    block.addGroup(index.length, group.length * 6, material)
    for (const f of group)
      for (let i = f * 6; i < f * 6 + 6; i++) index.push(source[i]!)
  })
  block.setIndex(index)
  return block
}

// Stacks on the table: on a wide screen a row of up to three, the rest
// centred in a row behind; on a phone, two columns.
function tableGrid(v: Viewport, series: number) {
  const perRow = v.mobile ? 2 : 3
  return { perRow, rows: Math.ceil(series / perRow) }
}

// The shelf: booklets stand in a row, each turned this far off square so
// the covers show side by side, spines one pitch apart (in booklet widths).
const SHELF_TURN = 36 * DEG
const SHELF_PITCH = 0.7
// The carousel: the centre booklet square to you; its neighbours turned
// CAROUSEL_TURN to face it, the first centred CAROUSEL_NEAR out and set
// CAROUSEL_BACK behind, the rest CAROUSEL_FAR apart. Their inner edges sit
// deeper than their outer ones, so each overlaps the next one out.
const CAROUSEL_NEAR = 0.98
const CAROUSEL_FAR = 0.3
const CAROUSEL_TURN = 56 * DEG
const CAROUSEL_BACK = 0.62
// The centre booklet stands this far forward, and its neighbours dim by up
// to this much, so the one in view carries the scene.
const CAROUSEL_LEAD = 0.1
const CAROUSEL_DIM = 0.32

export class CollectionStage {
  mode: StageMode = 'table'
  private series = 0
  private active = -1
  private focus = -1
  private readonly books: Book[] = []
  private readonly items: StageBook[]
  private readonly seriesCount: number
  // Each series' size, and each item's place in its series, known before
  // the scene is built.
  private readonly counts: number[]
  private readonly slots: number[]
  // A booklet the page landed on, brought into view once the rows are
  // measured.
  private startSlot = -1
  private renderer: WebGLRenderer | null = null
  private host: HTMLElement | null = null
  private readonly scene = new Scene()
  private readonly camera = new PerspectiveCamera(FOV, 1, 1, 1e5)
  private scrim!: Mesh<PlaneGeometry, MeshBasicMaterial>
  private sun!: DirectionalLight
  private ground!: Mesh
  private readonly raycaster = new Raycaster()
  private readonly ndc = new Vector2()
  private readonly targets: Object3D[] = []
  private readonly hits: Intersection[] = []
  // Which booklet each pickable mesh belongs to.
  private readonly owners = new Map<Object3D, Book>()
  private readonly shelf = new Scroller()
  private readonly carousel = new Scroller(true)
  private readonly pager = new WheelPager()
  private readonly morph = new Tween(0)
  private readonly deep = new Tween(0)
  private readonly fly = new Tween(0)
  private readonly open = new Tween(0)
  // When the flying booklet's cover starts to swing open (0: not waiting).
  private openAt = 0
  private v = makeViewport(1, 1)
  // Derived from the viewport, so measured on resize rather than per frame.
  private spread = spreadRect(this.v)
  private grid = tableGrid(this.v, 1)
  private pageW = 1
  // The frame loop runs only while something moves; 0 when it's stopped.
  private frame = 0
  private last = 0
  // Draw the next frame even if nothing moves (a resize, a texture landing).
  private dirty = false
  private resumed = false
  // The mouse, in CSS pixels; a touch doesn't hover.
  private readonly pointer = new Vector2()
  private hasPointer = false
  private cursor = ''
  private down: { x: number; y: number; moved: boolean } | null = null
  private seriesLift: number[]
  private seriesTilt: Vector2[]
  private spreadState: SpreadState = 'closed'
  // The right-hand side is bare (the back cover lies alone on the left).
  private bare = false
  // Closing was asked for while bare: the cover keeps turning the way it
  // was already facing instead of reversing over the first page, so the
  // page block (hidden the whole time) never flashes into view.
  private closingFromBack = false
  // Screen pixels per booklet step, so a drag moves the row under the
  // finger; and the shelf's first centred slot.
  private shelfPx = 1
  private carouselPx = 1
  private shelfFrom = 0
  private readonly tmp = {
    a: { pos: new Vector3(), quat: new Quaternion(), scale: new Vector3() },
    b: { pos: new Vector3(), quat: new Quaternion(), scale: new Vector3() },
    c: { pos: new Vector3(), quat: new Quaternion(), scale: new Vector3() },
    q: new Quaternion(),
    e: new Vector3(),
    o: new Vector3(),
    r: new Euler(),
    p: [new Vector3(), new Vector3(), new Vector3()],
    l: [new Vector3(), new Vector3(), new Vector3()],
  }

  constructor(
    items: StageBook[],
    seriesCount: number,
    private readonly listener: StageListener,
  ) {
    this.seriesCount = seriesCount
    this.seriesLift = Array.from({ length: seriesCount }, () => 0)
    this.seriesTilt = Array.from({ length: seriesCount }, () => new Vector2())
    const counts = Array.from({ length: seriesCount }, () => 0)
    for (const item of items) counts[item.series]!++
    const seen = Array.from({ length: seriesCount }, () => 0)
    this.items = items
    this.counts = counts
    this.slots = items.map((item) => seen[item.series]!++)
  }

  // Three and the DOM only exist in the browser, so the scene is built on
  // attach rather than when React first renders the stage on the server.
  private build() {
    const items = this.items
    const counts = this.counts
    const loader = new TextureLoader()
    const edges = pageEdges()
    const edge = new MeshStandardMaterial({ map: edges, roughness: 0.9 })
    const block = pageBlock()
    const face = new PlaneGeometry(1, 1 / ASPECT)
    face.translate(0.5, 0, 0)
    items.forEach((item, index) => {
      // Until its art lands the cover is its art's own colour, lit the same
      // way, so the art fades up out of it rather than out of black.
      const placeholder = toneTexture(item.tone)
      const showCover = (texture: Texture) => {
        coverFront.map = texture
        coverFront.emissiveMap = texture
        placeholder.dispose()
        this.wake()
      }
      const coverTexture: Texture = loader.load(
        item.cover,
        () => showCover(coverTexture),
        undefined,
        () => {
          const fallback = new CanvasTexture(fallbackCover(item))
          fallback.colorSpace = SRGBColorSpace
          showCover(fallback)
        },
      )
      coverTexture.colorSpace = SRGBColorSpace
      coverTexture.anisotropy = 8
      const coverFront = new MeshStandardMaterial({
        map: placeholder,
        emissiveMap: placeholder,
        emissive: new Color('#ffffff'),
        emissiveIntensity: 0.62,
        roughness: 0.5,
      })
      const spine = new MeshStandardMaterial({
        color: item.spine,
        roughness: 0.6,
      })
      // Unlit: the page under the cover and the cover's inside must match
      // the reader's pages exactly when they swap.
      const paper = new MeshBasicMaterial({
        color: item.paper,
        toneMapped: false,
      })
      const inside = new MeshBasicMaterial({
        color: item.front,
        toneMapped: false,
      })
      const group = new Group()
      const blockMesh = new Mesh(block, [edge, spine, paper])
      const cover = new Group()
      const front = new Mesh(face, coverFront)
      const back = new Mesh(face, inside)
      back.rotation.y = Math.PI
      back.position.x = 1
      cover.add(front, back)
      group.add(blockMesh, cover)
      // Booklets cast onto the table and each other, but the flat covers
      // don't take shadows: they'd only add acne to the printed art.
      for (const m of [blockMesh, front, back]) m.castShadow = true
      blockMesh.receiveShadow = true
      this.scene.add(group)
      const slot = this.slots[index]!
      const book: Book = {
        item,
        index,
        slot,
        count: counts[item.series]!,
        group,
        cover,
        inside,
        paper,
        face: coverFront,
        meshes: [blockMesh, front, back],
        seed: Math.sin(index * 12.9898 + 4.1) * 0.5,
        lift: 0,
        tilt: new Vector2(),
      }
      for (const mesh of book.meshes) this.owners.set(mesh, book)
      this.books.push(book)
    })

    this.scene.add(new HemisphereLight('#ffffff', '#b9b3a8', 1.1))
    const sun = new DirectionalLight('#ffffff', 1.1)
    sun.castShadow = true
    sun.shadow.mapSize.set(2048, 2048)
    sun.shadow.bias = -0.0006
    sun.shadow.normalBias = 0.02
    this.sun = sun
    this.scene.add(sun, sun.target)
    const ground = new Mesh(
      new PlaneGeometry(1, 1),
      new ShadowMaterial({ opacity: 0.2 }),
    )
    ground.rotation.x = -Math.PI / 2
    ground.receiveShadow = true
    this.ground = ground
    this.scene.add(ground)

    // Behind the reading booklet, in front of the table: fades the table
    // away while a booklet is open.
    this.scrim = new Mesh(
      new PlaneGeometry(1, 1),
      new MeshBasicMaterial({
        color: '#eeebe5',
        transparent: true,
        opacity: 0,
        depthWrite: false,
        toneMapped: false,
      }),
    )
    this.camera.add(this.scrim)
    this.scene.add(this.camera)
  }

  // Mode ------------------------------------------------------------------

  start(mode: StageMode, series: number, index: number) {
    this.mode = mode
    this.series = Math.max(0, series)
    this.active = mode === 'reading' ? index : -1
    this.focus = index
    this.morph.set(mode === 'table' ? 0 : 1)
    this.deep.set(mode === 'carousel' || mode === 'reading' ? 1 : 0)
    this.fly.set(mode === 'reading' ? 1 : 0)
    this.open.set(mode === 'reading' ? 1 : 0)
    this.shelf.reset(0)
    this.startSlot = index >= 0 ? (this.slots[index] ?? 0) : -1
    this.wake()
  }

  openSeries(series: number) {
    if (this.mode !== 'table' || series < 0 || series >= this.seriesCount)
      return
    this.series = series
    this.mode = 'shelf'
    this.shelf.reset(0)
    this.morph.go(1, MORPH_MS, performance.now())
    this.wake()
    this.listener.onMode('shelf', series, -1)
  }

  // Into the carousel on one booklet of the open series.
  showBook(index: number) {
    const book = this.books[index]
    if (!book || this.mode === 'reading' || this.mode === 'carousel') return
    if (this.mode === 'table' || book.item.series !== this.series) {
      this.series = book.item.series
      this.morph.set(1)
      this.centreShelf(book.slot)
    }
    this.mode = 'carousel'
    this.focus = index
    this.carousel.reset(book.slot)
    this.pager.reset()
    this.deep.go(1, DEEP_MS, performance.now())
    this.wake()
    this.listener.onMode('carousel', this.series, index)
  }

  toShelf() {
    if (this.mode === 'reading') return this.closeBook()
    if (this.mode !== 'carousel') return
    this.mode = 'shelf'
    const book = this.books[this.focus]
    if (book) this.centreShelf(book.slot)
    this.deep.go(0, DEEP_MS * 0.9, performance.now())
    this.wake()
    this.listener.onMode('shelf', this.series, this.focus)
  }

  toTable() {
    if (this.mode === 'reading') return this.closeBook()
    if (this.mode === 'carousel') return this.toShelf()
    if (this.mode !== 'shelf') return
    this.mode = 'table'
    this.morph.go(0, MORPH_MS * 0.85, performance.now())
    this.wake()
    this.listener.onMode('table', this.series, -1)
  }

  back() {
    if (this.mode === 'reading') this.closeBook()
    else if (this.mode === 'carousel') this.toShelf()
    else this.toTable()
  }

  openBook(index: number) {
    const book = this.books[index]
    if (!book || this.mode === 'reading') return
    if (this.mode !== 'carousel' || this.focus !== index) {
      // Straight from anywhere else: the carousel forms under it.
      if (this.mode === 'table' || book.item.series !== this.series) {
        this.series = book.item.series
        this.morph.set(1)
      }
      this.centreShelf(book.slot)
      this.carousel.reset(book.slot)
      this.deep.set(1)
    }
    this.focus = index
    this.active = index
    this.mode = 'reading'
    this.closingFromBack = false
    const now = performance.now()
    this.fly.go(1, FLY_MS, now)
    this.open.set(0)
    this.openAt = now + FLY_MS * 0.55
    this.wake()
    this.listener.onMode('reading', this.series, index)
  }

  closeBook() {
    if (this.mode !== 'reading') return
    // From the back cover, reversing the open swing would first un-hide the
    // page block, showing the first page's colour under a cover that's
    // still most of the way open. Instead keep the block hidden and let the
    // cover carry on turning the way it already faces, so it lands shut
    // from the back without ever exposing that page.
    this.closingFromBack = this.bare
    if (!this.closingFromBack) this.setBare(false)
    this.mode = 'carousel'
    this.openAt = 0
    this.open.go(0, OPEN_MS * 0.8, performance.now())
    this.wake()
    this.listener.onMode('carousel', this.series, this.active)
  }

  // Another booklet of the same series, picked inside the open one (its
  // catalogue pages): the booklet shuts and the carousel glides along to
  // the one picked. False when it isn't one this can reach that way.
  showOther(index: number) {
    const book = this.books[index]
    if (!book || this.mode !== 'reading' || book.item.series !== this.series)
      return false
    this.closeBook()
    this.carousel.nudge(book.slot - this.carousel.target)
    this.wake()
    return true
  }

  // The reader has turned the last leaf: the back cover lies on the left
  // with nothing beside it, so the open booklet's pages step aside.
  setBare(bare: boolean) {
    this.bare = bare
    const block = this.books[this.active]?.meshes[0]
    if (!block || block.visible === !bare) return
    block.visible = !bare
    this.wake()
  }

  // The open booklet's first page, painted by the reader, for the inside of
  // its cover (null clears it).
  setInside(index: number, canvas: HTMLCanvasElement | null) {
    const book = this.books[index]
    if (!book) return
    book.inside.map?.dispose()
    book.inside.map = null
    book.inside.color.set(this.bareInside(book))
    if (canvas) {
      const texture = new CanvasTexture(canvas)
      texture.colorSpace = SRGBColorSpace
      texture.generateMipmaps = false
      texture.minFilter = LinearFilter
      // The inside lies inset in the page it was painted for.
      const r = this.spread
      const ix = READING_INSET / r.pageW
      const iy = READING_INSET / r.pageH
      texture.repeat.set(1 - ix, 1 - 2 * iy)
      texture.offset.set(ix, iy)
      book.inside.map = texture
      book.inside.color.set('#ffffff')
    }
    book.inside.needsUpdate = true
    this.wake()
  }

  // The inside of a cover with nothing painted on it: in a spread, the
  // colour of the first page the reader is about to paint there; on a
  // phone, where the open cover lies off to the side, plain stock.
  private bareInside(book: Book) {
    return this.spread.single ? BLANK_INSIDE : book.item.front
  }

  private centreShelf(slot: number) {
    this.shelf.reset(
      Math.min(this.shelf.max, Math.max(0, slot - this.shelfFrom)),
    )
  }

  // Lifecycle -------------------------------------------------------------

  attach(host: HTMLElement) {
    this.host = host
    if (!this.books.length) this.build()
    const renderer = new WebGLRenderer({ antialias: true, alpha: true })
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio))
    renderer.outputColorSpace = SRGBColorSpace
    renderer.shadowMap.enabled = true
    renderer.shadowMap.type = PCFShadowMap
    renderer.domElement.className = 'bricks-canvas'
    host.prepend(renderer.domElement)
    this.renderer = renderer
    this.cursor = ''
    this.resize()
  }

  detach() {
    cancelAnimationFrame(this.frame)
    this.frame = 0
    // three r184's dispose() frees GPU resources but doesn't release the
    // context itself; forceContextLoss does (model-viewer.ts's unmount does
    // the same for the 3D build view).
    this.renderer?.dispose()
    this.renderer?.forceContextLoss()
    this.renderer?.domElement.remove()
    this.renderer = null
    const disposed = new Set<unknown>()
    for (const book of this.books)
      for (const mesh of book.meshes) {
        const materials = (
          Array.isArray(mesh.material) ? mesh.material : [mesh.material]
        ) as Material[]
        for (const m of materials) {
          if (disposed.has(m)) continue
          disposed.add(m)
          ;(m as MeshStandardMaterial).map?.dispose()
          m.dispose()
        }
        if (!disposed.has(mesh.geometry)) {
          disposed.add(mesh.geometry)
          mesh.geometry.dispose()
        }
      }
  }

  // The page behind the canvas, for the scrim to fade into.
  setBackground(color: string) {
    this.scrim.material.color.set(color)
    this.wake()
  }

  resize() {
    const renderer = this.renderer
    const host = this.host
    if (!renderer || !host) return
    const w = host.clientWidth || window.innerWidth
    const h = host.clientHeight || window.innerHeight
    renderer.setSize(w, h)
    this.v = makeViewport(w, h)
    this.spread = spreadRect(this.v)
    this.grid = tableGrid(this.v, this.seriesCount)
    this.camera.aspect = w / h
    this.camera.fov = FOV
    // Depth precision goes with far / near. The page block sits a fraction
    // of a pixel under its cover, so a loose range lets them fight through
    // each other; everything drawn lies within a few focal lengths.
    this.camera.near = this.focal() / 20
    this.camera.far = this.focal() * 20
    this.camera.updateProjectionMatrix()
    this.pageW = this.spread.pageW
    // A phone opens on the first page rather than a spread.
    for (const book of this.books) {
      book.paper.color.set(
        this.spread.single ? book.item.front : book.item.paper,
      )
      if (!book.inside.map) book.inside.color.set(this.bareInside(book))
    }
    // The scrim fills the view just behind the reading booklet.
    const d = this.focal() + this.pageW * THICK * 3
    const sh = 2 * d * Math.tan((FOV / 2) * DEG) * 1.2
    this.scrim.position.set(0, 0, -d)
    this.scrim.scale.set(sh * this.camera.aspect, sh, 1)
    const ground = this.pageW * 60
    this.ground.scale.set(ground, ground, 1)
    const shadow = this.sun.shadow.camera
    const reach = this.pageW * 9
    shadow.left = shadow.bottom = -reach
    shadow.right = shadow.top = reach
    shadow.near = 1
    shadow.far = this.pageW * 30
    shadow.updateProjectionMatrix()
    this.sun.position.set(-this.pageW * 4, this.pageW * 12, this.pageW * 6)
    this.sun.target.position.set(0, 0, 0)
    this.wake()
  }

  // Runs the frame loop until everything settles. `draw` draws a frame even
  // if nothing turns out to be moving; input that may not change the picture
  // (the mouse passing over empty table) only checks.
  private wake(draw = true) {
    if (draw) this.dirty = true
    if (this.frame || !this.renderer) return
    this.resumed = true
    this.frame = requestAnimationFrame(this.loop)
  }

  private readonly loop = (now: number) => {
    this.frame = 0
    if (this.tick(now)) this.frame = requestAnimationFrame(this.loop)
  }

  // Distance at which a world unit is a CSS pixel.
  private focal() {
    return this.v.height / 2 / Math.tan((FOV / 2) * DEG)
  }

  // Layouts ---------------------------------------------------------------

  private stackCenter(series: number, out: Vector3) {
    const B = this.pageW
    const { perRow, rows } = this.grid
    const row = Math.floor(series / perRow)
    const inRow = Math.min(perRow, this.seriesCount - row * perRow)
    const col = series - row * perRow
    const pitchX = this.v.mobile ? 1.45 : 1.62
    const pitchZ = this.v.mobile ? 1.75 : 1.8
    return out.set(
      (col - (inRow - 1) / 2) * B * pitchX,
      0,
      (row - (rows - 1) / 2) * B * pitchZ,
    )
  }

  private stackPose(book: Book, out: Pose) {
    const B = this.pageW
    const s = book.item.series
    this.stackCenter(s, out.pos)
    const level = book.count - 1 - book.slot
    const lift = this.seriesLift[s]!
    const top = book.slot === 0
    // Spine-left origin; lying face up, the cover's top edge away from you.
    out.pos.x += -B / 2 + book.seed * B * 0.05
    out.pos.z += Math.cos(book.seed * 9) * B * 0.03 - B * 0.06
    out.pos.y = (level + 1) * B * THICK * 1.04 + lift * B * (top ? 1.6 : 1)
    const tilt = this.seriesTilt[s]!
    const k = top ? 1 : 0.2
    this.tmp.e.set(-Math.PI / 2 + tilt.x * k, book.seed * 0.12, tilt.y * k)
    out.quat.setFromEuler(
      this.tmp.r.set(this.tmp.e.x, this.tmp.e.y, this.tmp.e.z, 'YXZ'),
    )
    out.scale.setScalar(B)
    return out
  }

  // A booklet standing on the table, its centre at (x, z), turned about its
  // vertical centre line. The group's origin is the spine's midpoint.
  private standing(x: number, z: number, turn: number, out: Pose) {
    const B = this.pageW
    out.quat.setFromEuler(this.tmp.r.set(0, turn, 0))
    this.tmp.o.set(B / 2, 0, 0).applyQuaternion(out.quat)
    out.pos.set(x - this.tmp.o.x, B / ASPECT / 2, z - this.tmp.o.z)
    out.scale.setScalar(B)
    return out
  }

  private shelfPose(book: Book, out: Pose) {
    const B = this.pageW
    const x = (book.slot - this.shelfFrom - this.shelf.pos) * SHELF_PITCH * B
    const lift = book.lift
    this.standing(x, lift * B * 0.28, SHELF_TURN * (1 - lift * 0.45), out)
    out.pos.y += lift * B * 0.04
    return out
  }

  private carouselPose(book: Book, out: Pose) {
    const B = this.pageW
    const k = book.slot - this.carousel.pos
    const side = Math.sign(k)
    const near = Math.min(1, Math.abs(k))
    const rest = Math.max(0, Math.abs(k) - 1)
    // Past a few booklets out the rest slide away off the side.
    const gone = Math.max(0, Math.abs(k) - 3.5)
    const x = side * (near * CAROUSEL_NEAR + rest * CAROUSEL_FAR + gone * 2) * B
    const z =
      ((1 - near) * CAROUSEL_LEAD - near * CAROUSEL_BACK - rest * 0.1) * B
    this.standing(x, z, -side * near * CAROUSEL_TURN, out)
    // The centre booklet leans a touch toward the pointer.
    if (near < 1) {
      const t = book.tilt
      this.tmp.q.setFromEuler(this.tmp.r.set(t.x, t.y, 0))
      const centre = this.tmp.o.set(B / 2, 0, 0).applyQuaternion(out.quat)
      centre.add(out.pos)
      out.quat.premultiply(this.tmp.q)
      out.pos
        .copy(centre)
        .sub(this.tmp.e.set(B / 2, 0, 0).applyQuaternion(out.quat))
    }
    return out
  }

  // Facing the camera at the focal distance, over the reader's right page.
  private readingPose(out: Pose) {
    const r = this.spread
    const F = this.focal()
    const hinge = this.v.width / 2 + r.hinge
    const local = this.tmp.e.set(
      hinge - this.v.width / 2,
      this.v.height / 2 - (r.top + r.pageH / 2),
      -F,
    )
    this.camera.updateMatrixWorld()
    out.pos.copy(local).applyMatrix4(this.camera.matrixWorld)
    out.quat.copy(this.camera.quaternion)
    const w = r.pageW - READING_INSET
    out.scale.set(w, (r.pageH - 2 * READING_INSET) * ASPECT, w)
    return out
  }

  private tableCamera(pos: Vector3, look: Vector3) {
    const B = this.pageW
    const mobile = this.v.mobile
    const { perRow, rows } = this.grid
    const cols = Math.min(perRow, this.seriesCount)
    const width = B * (cols * (mobile ? 1.45 : 1.62) + 0.3)
    const depth = B * (rows * (mobile ? 1.75 : 1.8) + 0.3)
    this.frameCamera(width, depth, mobile ? 64 : 58, pos, look)
  }

  // How far off the shelf and carousel cameras sit: far enough that a
  // booklet fills the given share of the screen's height (or, on a phone,
  // width).
  private shelfDistance() {
    const B = this.pageW
    return this.v.mobile
      ? (B * this.focal()) / (this.v.width * 0.4)
      : ((B / ASPECT) * this.focal()) / (this.v.height * 0.4)
  }

  private carouselDistance() {
    const B = this.pageW
    return this.v.mobile
      ? (B * this.focal()) / (this.v.width * 0.7)
      : ((B / ASPECT) * this.focal()) / (this.v.height * 0.7)
  }

  // Low over the table, looking at the booklets' middle.
  private standingCamera(
    distance: number,
    elevation: number,
    pos: Vector3,
    look: Vector3,
  ) {
    const e = elevation * DEG
    look.set(0, this.pageW / ASPECT / 2, 0)
    pos.set(0, look.y + Math.sin(e) * distance, Math.cos(e) * distance)
  }

  // A camera looking down at `elevation` degrees that fits a width x depth
  // patch of table centred on the origin.
  private frameCamera(
    width: number,
    depth: number,
    elevation: number,
    pos: Vector3,
    look: Vector3,
  ) {
    const vfov = FOV * DEG
    const hfov = 2 * Math.atan(Math.tan(vfov / 2) * this.camera.aspect)
    const e = elevation * DEG
    const byWidth = width / 2 / Math.tan(hfov / 2)
    const byDepth = (depth * Math.sin(e)) / 2 / Math.tan(vfov / 2)
    const d = Math.max(byWidth, byDepth) * 1.22
    // Aimed a little behind centre: the stacks' height lifts them toward
    // the top of the frame.
    look.set(0, 0, -depth * 0.04)
    pos.set(0, Math.sin(e) * d, Math.cos(e) * d + look.z)
  }

  // Input -----------------------------------------------------------------

  wheel(delta: number) {
    if (this.mode === 'shelf') this.shelf.nudge(delta / this.shelfPx)
    if (this.mode === 'carousel') {
      const step = this.pager.push(delta)
      if (step) this.carousel.nudge(step)
    }
    this.wake(false)
  }

  key(key: string) {
    const next = key === 'ArrowRight' || key === 'ArrowDown'
    const prev = key === 'ArrowLeft' || key === 'ArrowUp'
    if (this.mode === 'shelf') {
      if (next) this.shelf.nudge(1)
      if (prev) this.shelf.nudge(-1)
    }
    if (this.mode === 'carousel') {
      if (next) this.carousel.nudge(1)
      if (prev) this.carousel.nudge(-1)
      if (key === 'Enter') this.openBook(this.focus)
    }
    this.wake(false)
  }

  private get scroller() {
    return this.mode === 'shelf'
      ? this.shelf
      : this.mode === 'carousel'
        ? this.carousel
        : null
  }

  // Only a mouse hovers; a touch that lifted booklets would feel sticky.
  pointerDown(x: number, y: number, mouse: boolean) {
    this.down = { x, y, moved: false }
    this.setPointer(x, y, mouse)
    this.scroller?.grab(performance.now())
    this.wake(false)
  }

  pointerMove(x: number, y: number, mouse: boolean) {
    this.setPointer(x, y, mouse)
    const down = this.down
    if (!down) {
      this.wake(false)
      return
    }
    const dx = x - down.x
    if (Math.hypot(dx, y - down.y) > 6) down.moved = true
    const scroller = this.scroller
    if (scroller?.dragging)
      scroller.drag(
        -dx / (this.mode === 'shelf' ? this.shelfPx : this.carouselPx),
        performance.now(),
      )
    this.wake()
  }

  private setPointer(x: number, y: number, mouse: boolean) {
    this.hasPointer = mouse
    this.pointer.set(x, y)
  }

  pointerUp(x: number, y: number) {
    const down = this.down
    this.down = null
    if (this.shelf.dragging) this.shelf.release()
    if (this.carousel.dragging) this.carousel.release()
    this.wake(false)
    if (!down || down.moved) return
    const hit = this.pick(x, y)
    if (!hit) return
    if (this.mode === 'table' && !this.morph.running)
      this.openSeries(hit.item.series)
    else if (this.mode === 'shelf' && hit.item.series === this.series)
      this.showBook(hit.index)
    else if (this.mode === 'carousel' && hit.item.series === this.series) {
      if (hit.index === this.focus && !this.deep.running)
        this.openBook(hit.index)
      else this.carousel.nudge(hit.slot - this.carousel.target)
    }
  }

  pointerLeave() {
    this.hasPointer = false
    this.wake(false)
  }

  // Runs on every drawn frame the mouse is over the stage, so it reuses its
  // arrays rather than building new ones.
  private pick(x: number, y: number) {
    if (!this.renderer) return null
    this.ndc.set((x / this.v.width) * 2 - 1, -(y / this.v.height) * 2 + 1)
    this.raycaster.setFromCamera(this.ndc, this.camera)
    const targets = this.targets
    targets.length = 0
    for (const b of this.books)
      if (
        b.group.visible &&
        (this.mode === 'table' || b.item.series === this.series)
      )
        for (const mesh of b.meshes) targets.push(mesh)
    const hits = this.hits
    hits.length = 0
    this.raycaster.intersectObjects(targets, false, hits)
    const hit = hits[0]
    hits.length = 0
    return hit ? (this.owners.get(hit.object) ?? null) : null
  }

  // Frame -----------------------------------------------------------------

  // Advances and draws one frame. Returns whether anything is still moving;
  // once nothing is, the loop stops until input or a change wakes it.
  private tick(now: number) {
    const renderer = this.renderer
    if (!renderer) return false
    // Resuming after a pause steps one ordinary frame, not the whole pause.
    const dt = this.resumed ? 1 / 60 : Math.min(0.05, (now - this.last) / 1000)
    this.resumed = false
    this.last = now
    const tweening = this.stepTweens(now)
    this.measure()
    if (this.startSlot >= 0) {
      this.centreShelf(this.startSlot)
      this.carousel.reset(this.startSlot)
      this.startSlot = -1
    }
    const shelfMoving = this.shelf.step(dt)
    const carouselMoving = this.carousel.step(dt)
    if (this.mode === 'carousel') this.settleFocus()
    this.placeCamera()
    const hovering = this.hover(dt)
    const swing = easeOut(this.open.value)
    this.placeBooks(swing)
    this.emitSpread(swing)
    const moving =
      tweening ||
      this.fly.running ||
      this.open.running ||
      this.openAt > 0 ||
      shelfMoving ||
      carouselMoving ||
      hovering
    if (moving || this.dirty) renderer.render(this.scene, this.camera)
    this.dirty = false
    return moving
  }

  // Advances the view tweens. Returns whether any was running: one that
  // ends this frame still has its last step to draw.
  private stepTweens(now: number) {
    const running =
      this.morph.running ||
      this.deep.running ||
      this.fly.running ||
      this.open.running ||
      this.openAt > 0
    this.morph.update(now)
    this.deep.update(now)
    this.fly.update(now)
    if (this.openAt && now >= this.openAt) {
      this.openAt = 0
      this.open.go(1, OPEN_MS, now)
    }
    this.open.update(now)
    // A closed booklet flies home once its cover is shut.
    if (
      this.mode !== 'reading' &&
      this.open.value <= 0 &&
      this.fly.target > 0 &&
      !this.open.running
    ) {
      // The cover has finished turning from the back: it's shut now, so
      // the page block goes back to its ordinary hidden-until-bare state.
      if (this.closingFromBack) {
        this.closingFromBack = false
        this.setBare(false)
      }
      this.fly.go(0, FLY_MS, now)
    }
    return running
  }

  // Table, down to the shelf as a series stands up, then in close for the
  // carousel.
  private placeCamera() {
    const m = ease(this.morph.value)
    const d = ease(this.deep.value)
    const [tp, sp, cp] = this.tmp.p as [Vector3, Vector3, Vector3]
    const [tl, sl, cl] = this.tmp.l as [Vector3, Vector3, Vector3]
    this.tableCamera(tp, tl)
    this.standingCamera(this.shelfDistance(), 9, sp, sl)
    this.standingCamera(this.carouselDistance(), 4, cp, cl)
    this.camera.position.lerpVectors(tp, sp, m).lerp(cp, d)
    this.camera.lookAt(tl.lerp(sl, m).lerp(cl, d))
    this.camera.updateMatrixWorld()
  }

  // Every booklet's pose for this frame, blended between the views, with
  // the open booklet flown to the reading pose and its cover swung open.
  private placeBooks(swing: number) {
    const flyE = ease(this.fly.value)
    this.scrim.material.opacity = smoothstep(0.1, 0.8, this.fly.value)
    this.scrim.visible = this.scrim.material.opacity > 0.001

    const a = this.tmp.a
    const b = this.tmp.b
    const c = this.tmp.c
    // The other series are off the table once it has part turned.
    const cleared = this.morph.value * 1.35 >= 1
    // Once the scrim is solid only the open booklet shows. On a short,
    // wide screen the carousel's neighbours stand nearer the camera than
    // the scrim, so they'd otherwise stay in view beside the open pages.
    const covered = this.scrim.material.opacity > 0.999
    for (const book of this.books) {
      const mine = book.item.series === this.series
      const g = book.group
      if (mine) {
        this.stackPose(book, a)
        this.shelfPose(book, b)
        // The top of the stack goes first; each arcs up as it stands.
        const delay = (book.slot / Math.max(1, book.count)) * 0.34
        const local = clamp01((this.morph.value - delay) / 0.66)
        const e = ease(local)
        g.position.lerpVectors(a.pos, b.pos, e)
        g.position.y += Math.sin(Math.PI * e) * this.pageW * 0.55
        g.quaternion.slerpQuaternions(a.quat, b.quat, e)
        g.scale.copy(b.scale)
        if (this.deep.value > 0) {
          // Nearest the chosen booklet first.
          this.carouselPose(book, c)
          const k = Math.abs(book.slot - this.carousel.pos)
          const lag = Math.min(1, k / 6) * 0.22
          const t = ease(clamp01((this.deep.value - lag) / (1 - lag)))
          g.position.lerp(c.pos, t)
          g.quaternion.slerp(c.quat, t)
        }
        const dim =
          ease(this.deep.value) *
          Math.min(1, Math.abs(book.slot - this.carousel.pos)) *
          CAROUSEL_DIM
        book.face.color.setScalar(1 - dim)
        book.face.emissiveIntensity = 0.62 * (1 - dim)
        g.visible = !covered || book.index === this.active
      } else if (cleared) {
        g.visible = false
      } else {
        // The other series slide off the table.
        this.stackPose(book, a)
        const e = easeOut(clamp01(this.morph.value * 1.35))
        const side = book.item.series < this.series ? -1 : 1
        g.position.copy(a.pos)
        if (this.v.mobile) g.position.z += side * e * this.pageW * 8
        else g.position.x += side * e * this.pageW * 9
        g.quaternion.copy(a.quat)
        g.scale.copy(a.scale)
        g.visible = e < 0.999
      }
      book.cover.rotation.y = 0
      if (book.index === this.active && this.fly.value > 0) {
        const from = this.tmp.c
        from.pos.copy(g.position)
        from.quat.copy(g.quaternion)
        from.scale.copy(g.scale)
        this.readingPose(b)
        g.position.lerpVectors(from.pos, b.pos, flyE)
        g.quaternion.slerpQuaternions(from.quat, b.quat, flyE)
        g.scale.lerpVectors(from.scale, b.scale, flyE)
        // Closing from the back keeps turning the same way it was already
        // facing (through another half turn) instead of reversing, so it
        // never passes back over the first page on its way shut.
        book.cover.rotation.y = this.closingFromBack
          ? -(2 - swing) * Math.PI
          : -swing * Math.PI
      }
    }
  }

  // Tells the reader how much of it the swinging cover has uncovered.
  private emitSpread(swing: number) {
    const closing = this.open.target < 1
    const painted = !!this.books[this.active]?.inside.map
    const reading = this.fly.value > 0.999
    const state: SpreadState = !reading
      ? 'closed'
      : swing > (closing ? 0.985 : painted ? 0.99999 : 0.9)
        ? 'open'
        : swing > (closing ? 0.7 : 0.55)
          ? 'right'
          : 'closed'
    if (state !== this.spreadState) {
      this.spreadState = state
      this.listener.onSpread(state)
    }
  }

  // The carousel's booklet is whichever it's heading for.
  private settleFocus() {
    const slot = Math.round(this.carousel.target)
    const book = this.books.find(
      (b) => b.item.series === this.series && b.slot === slot,
    )
    if (!book || book.index === this.focus) return
    this.focus = book.index
    this.listener.onFocus(book.index)
  }

  // Scroll ranges and drag ratios for the open series, per frame since the
  // series and the viewport can both change.
  private measure() {
    const B = this.pageW
    const F = this.focal()
    const count = this.counts[this.series] ?? 0
    const shelfD = this.shelfDistance()
    this.shelfPx = Math.max(1, (SHELF_PITCH * B * F) / shelfD)
    this.carouselPx = Math.max(
      1,
      (CAROUSEL_NEAR * B * F) / this.carouselDistance(),
    )
    // Booklets that fit either side of the centre, less a margin.
    const half = (this.v.width * shelfD) / F / 2 / (SHELF_PITCH * B) - 1.3
    const from = Math.min((count - 1) / 2, half)
    this.shelfFrom = from
    this.shelf.max = Math.max(0, count - 1 - 2 * from)
    this.carousel.max = Math.max(0, count - 1)
  }

  // Eases the hover lifts and leans toward the pointer. Returns whether any
  // of them is still on its way.
  private hover(dt: number) {
    const p = this.hasPointer ? this.pointer : null
    const idle = !this.morph.running && !this.deep.running && !this.down
    let series = -1
    let book = -1
    if (p && idle && this.mode !== 'reading') {
      const hit = this.pick(p.x, p.y)
      if (hit && this.mode === 'table') series = hit.item.series
      if (hit && this.mode !== 'table') book = hit.index
    }
    const cursor = series >= 0 || book >= 0 ? 'pointer' : ''
    if (this.host && cursor !== this.cursor) {
      this.cursor = cursor
      this.host.style.cursor = cursor
    }
    const px = p ? (p.x / this.v.width) * 2 - 1 : 0
    const py = p ? -(p.y / this.v.height) * 2 + 1 : 0
    const k12 = approach(dt, 12)
    const k11 = approach(dt, 11)
    const k10 = approach(dt, 10)
    const k8 = approach(dt, 8)
    let gap = 0
    for (let s = 0; s < this.seriesCount; s++) {
      const on = s === series ? 1 : 0
      const lift = on * 0.07 - this.seriesLift[s]!
      this.seriesLift[s]! += lift * k12
      const tilt = this.seriesTilt[s]!
      const tx = (on ? 0.05 - py * 0.1 : 0) - tilt.x
      const ty = (on ? -px * 0.08 : 0) - tilt.y
      tilt.x += tx * k10
      tilt.y += ty * k10
      gap = Math.max(gap, Math.abs(lift), Math.abs(tx), Math.abs(ty))
    }
    const shelf = this.mode === 'shelf'
    for (const b of this.books) {
      // On the shelf a hovered booklet pulls forward; in the carousel only
      // the centre one leans.
      const on = shelf && b.index === book ? 1 : 0
      const lift = on - b.lift
      b.lift += lift * k11
      const lean =
        this.mode === 'carousel' && b.index === book && b.index === this.focus
      const tx = (lean ? -py * 0.12 : 0) - b.tilt.x
      const ty = (lean ? px * 0.16 : 0) - b.tilt.y
      b.tilt.x += tx * k8
      b.tilt.y += ty * k8
      gap = Math.max(gap, Math.abs(lift), Math.abs(tx), Math.abs(ty))
    }
    return gap > SETTLED
  }
}
